import React, {useContext, useMemo, useState} from 'react';
import {Table, Tag} from 'antd';
import {useFetchShoppingRoadGroup} from "./useShoppingRoad";
import {useItemKrTextData} from "../../probability/useItemData";
import {AuthContext} from "../../../shared/contexts/authContext";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import TablePage from "../../../pages/tablePage/TablePage";
import ShoppingRoadEventEditModal from "./ShoppingRoadEventEditModal";
import {parseRewards} from "../../../shared/utils/shoppingRoadUtils";

const ShoppingRoadEventGroup = () => {
    const {currentUser} = useContext(AuthContext);
    const {data, isLoading, error} = useFetchShoppingRoadGroup();
    const {data: itemRaw} = useItemKrTextData();
    const itemData = itemRaw?.[currentUser?.target] || [];
    const [rowData, setRowData] = useState(null);

    const groupedData = useMemo(() => {
        if (!data?.rows) return [];
        const groups = {};
        data.rows.forEach(row => {
            if (!groups[row.groupIdx]) {
                groups[row.groupIdx] = {groupIdx: row.groupIdx, items: [], key: row.groupIdx, isFixed: row.isFixed};
            }
            groups[row.groupIdx].items.push(row);
        });
        return Object.values(groups).sort((a, b) => a.groupIdx - b.groupIdx);
    }, [data]);

    const columns = useMemo(() => ([
        {
            title: '그룹 번호',
            dataIndex: 'groupIdx',
            key: 'groupIdx',
            search: true,
        },
        {
            title: '메모',
            key: 'memo',
            render: (_, record) => record.items?.[0]?.memo || '-',
        },
    ]), []);

    const expandedRowRender = useMemo(() => (record) => {
        const itemCols = [
            {title: '순서', dataIndex: 'groupSeq', key: 'groupSeq', width: 80},
            {
                title: '난이도',
                dataIndex: 'difficulty',
                key: 'difficulty',
                width: 90,
                render: d => ({1: '쉬움', 2: '보통', 3: '어려움'}[d] || d),
            },
            {
                title: '뱃지',
                dataIndex: 'isNew',
                key: 'isNew',
                render: d => ({0: '없음', 1: '신규'}[d] || d),
            },
            {
                title: '타입',
                dataIndex: 'groupType',
                key: 'groupType',
                width: 90,
                render: t => ({1: '이벤트', 2: '보상'}[t] || t),
            },
            {
                title: '이벤트 번호',
                dataIndex: 'eventIdx',
                key: 'eventIdx',
                width: 100,
                render: v => v || '-',
            },
            {
                title: '상자 종류',
                dataIndex: 'boxType',
                key: 'boxType',
                width: 80,
                render: v => ({0: '-', 1: '일반', 2: '최종'}[v] || v),
            },
            {
                title: '보상',
                dataIndex: 'rewards',
                key: 'rewards',
                render: (r) => {
                    const rewards = parseRewards(r);
                    if (!rewards.length) return '-';
                    return rewards.map((rw, i) => {
                        const item = itemData.find(d => d.index === rw.id && d.itemType === rw.type);
                        const label = item ? `${item.name}(${rw.id})` : `${rw.id}`;
                        return <Tag key={i}>{label} x {rw.quantity}</Tag>;
                    });
                },
            },
        ];

        return (
            <Table
                columns={itemCols}
                dataSource={record.items.map((it, i) => ({...it, key: i}))}
                pagination={false}
                size="small"
            />
        );
    }, [itemData]);

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;

    const props = {
        columns: columns,
        localData: groupedData,
        modal: ShoppingRoadEventEditModal,
        buttonText: '신규 쇼핑 로드 이벤트 그룹 추가',
        rowData: rowData,
        setRowData: setRowData,
        expandedRowRender: expandedRowRender,
        modalWidth: 1400,
        modalResizable: true,
        modalOptions: {
            tbl: data?.tbl || {},
            allRows: data?.rows || [],
        },
    };

    return (
        <div>
            <TablePage {...props}/>
        </div>
    );
};

export default ShoppingRoadEventGroup;